// ==============================|| REACT & THIRD-PARTY IMPORTS ||============================== //
import React, { useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Divider,
    Typography,
    Stack,
    InputLabel,
    IconButton,
    useTheme,
    CircularProgress
} from '@mui/material';
import { CloseOutlined, SaveOutlined } from '@ant-design/icons';
import { FormikProvider, useFormik, Form } from 'formik';
import * as Yup from 'yup';

// ==============================|| CONTEXT & HOOKS ||============================== //
import { useFileManagerState } from 'hooks/useFileManager';
import { UpdateFileNotesformProps, UpdateFileNotesformValues } from 'types/CommonFileManager/commonFileManager';



export default function UpdateFileNotesform({
    open: propOpen,
    onClose: propOnClose,
    onSubmit: propOnSubmit,
    fileId: propFileId,
    fileName: propFileName,
    initialNote: propInitialNote
}: UpdateFileNotesformProps) {
    const state = useFileManagerState();

    const open = propOpen ?? state.isNoteOpen;
    const onClose = propOnClose ?? (() => {
        state.setIsNoteOpen(false);
        state.setActiveNoteItem(null);
    });
    const onSubmit = propOnSubmit ?? state.handleNoteSubmit;
    const fileId = propFileId ?? state.activeNoteItem?.id ?? '';
    const fileName = propFileName ?? state.activeNoteItem?.name ?? '';
    const initialNote = propInitialNote ?? state.activeNoteItem?.note ?? '';

    const theme = useTheme();

    const NoteSchema = Yup.object().shape({
        note: Yup.string()
            .max(6000, 'Note must be at most 6000 characters')
    });

    const formik = useFormik<UpdateFileNotesformValues>({
        initialValues: {
            note: initialNote
        },
        enableReinitialize: true,
        validateOnBlur: false,
        validationSchema: NoteSchema,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                await onSubmit(fileId, values.note.trim());
                onClose();
            } catch (err) {
                console.error(err);
            } finally {
                setSubmitting(false);
            }
        }
    });

    useEffect(() => {
        formik.resetForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialNote]);

    const handleClose = () => {
        if (formik.isSubmitting) return;
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (formik.isSubmitting) return;
                if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                    onClose();
                }
            }}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                    overflow: 'hidden'
                }
            }}
        >
            <FormikProvider value={formik}>
                <Form noValidate onSubmit={formik.handleSubmit}>
                    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" component="div">
                            <Typography component="span" sx={{ color: 'primary.main' }}>
                                Update
                            </Typography>{' '}
                            Note
                        </Typography>
                        <IconButton
                            type="button"
                            aria-label="close"
                            onClick={handleClose}
                            disabled={formik.isSubmitting}
                            sx={{
                                color: (theme) => theme.palette.primary.main
                            }}
                        >
                            <CloseOutlined />
                        </IconButton>
                    </DialogTitle>
                    <Divider />

                    <DialogContent sx={{ p: 2.5 }}>
                        <Stack sx={{ gap: 1 }}>
                            <InputLabel htmlFor="file-note">Note for <strong style={{ fontWeight: '500', color: theme.palette.text.primary }}>{fileName}</strong></InputLabel>
                            <TextField
                                autoFocus
                                fullWidth
                                multiline
                                rows={8}
                                id="file-note"
                                placeholder="Enter your note here..."
                                {...formik.getFieldProps('note')}
                                error={Boolean(formik.touched.note && formik.errors.note)}
                                slotProps={{ htmlInput: { maxLength: 4000 } }}
                                helperText={formik.touched.note && formik.errors.note ? (formik.errors.note as string) : `${formik.values.note.length}/4000 characters`}
                                disabled={formik.isSubmitting}
                            />
                        </Stack>
                    </DialogContent>
                    <Divider />

                    <DialogActions sx={{ p: 2 }}>
                        <Stack direction="row" sx={{ gap: 2, alignItems: 'center', width: 1, justifyContent: 'flex-end' }}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={formik.isSubmitting}
                                startIcon={
                                    formik.isSubmitting ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : (
                                        <SaveOutlined />
                                    )
                                }
                            >
                                Update
                            </Button>
                        </Stack>
                    </DialogActions>
                </Form>
            </FormikProvider>
        </Dialog>
    );
}
